/**
 * EPIC-DM01: Demo Intake Page
 * Collects prospect information and legal consents during demo session
 * 
 * Design Reference: Figma Intake (iPad + phone)
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDemoMode, type DemoSession, type IntakePayload } from '@/hooks/useDemoMode';
import { IntakeForm } from '@/components/demo/IntakeForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';

export default function DemoIntake() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    canAccessDemoMode,
    isDemoModeEnabled,
    isLoading: isLoadingConfig,
    submitIntake,
    isSubmittingIntake,
    submitIntakeError,
    getSession,
  } = useDemoMode();

  const [session, setSession] = useState<DemoSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Load session on mount
  useEffect(() => {
    async function loadSession() {
      if (!sessionId) {
        setSessionError('No session ID provided');
        setIsLoadingSession(false);
        return;
      }

      try {
        const loadedSession = await getSession(sessionId);
        if (!loadedSession) {
          setSessionError('Session not found');
        } else if (loadedSession.status !== 'opened' && loadedSession.status !== 'intake') {
          setSessionError(`Session is in "${loadedSession.status}" state and cannot accept intake`);
        } else {
          setSession(loadedSession);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        setSessionError('Failed to load session');
      } finally {
        setIsLoadingSession(false);
      }
    }

    loadSession();
  }, [sessionId, getSession]);

  // Handle form submission
  const handleSubmit = async (data: IntakePayload) => {
    try {
      await submitIntake(data);
      // Navigate to capture page (DM01-C will implement this)
      // For now, navigate to a placeholder success state
      navigate(`/demo/capture/${sessionId}`);
    } catch (err) {
      // Error is handled by the form via submitIntakeError
      console.error('Intake submission failed:', err);
    }
  };

  // Loading state
  if (isLoadingConfig || isLoadingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authorized
  if (!canAccessDemoMode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Not Authorized
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You do not have permission to access Demo Mode.
            </p>
            <Button 
              className="mt-4 w-full" 
              variant="outline"
              onClick={() => navigate('/home')}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Demo mode disabled
  if (!isDemoModeEnabled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Demo Mode Disabled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Demo Mode is currently disabled.
            </p>
            <Button 
              className="mt-4 w-full" 
              variant="outline"
              onClick={() => navigate('/home')}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Session error
  if (sessionError || !session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Session Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {sessionError || 'Unable to load session'}
            </p>
            <Button 
              className="mt-4 w-full" 
              variant="outline"
              onClick={() => navigate('/demo')}
            >
              Return to Demo Landing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/demo')}
            title="Back to Demo Landing"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Prospect Intake</h1>
            <p className="text-sm text-muted-foreground">
              Step 1 of 3: Enter your information
            </p>
          </div>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="bg-muted/30 border-b">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary" />
              <span className="text-sm font-medium">Intake</span>
            </div>
            <div className="flex-1 h-0.5 bg-border" />
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-border" />
              <span className="text-sm text-muted-foreground">Record</span>
            </div>
            <div className="flex-1 h-0.5 bg-border" />
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-border" />
              <span className="text-sm text-muted-foreground">Results</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-xl">
        <IntakeForm
          sessionId={session.id}
          onSubmit={handleSubmit}
          isSubmitting={isSubmittingIntake}
          error={submitIntakeError}
        />
      </main>
    </div>
  );
}
